import { createFileRoute } from "@tanstack/react-router";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, collectionPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { PricingPage } from "@/components/pages/PricingPage";

const SITE = "https://nyclanguageinstitute.com";
const TITLE =
  "ราคาบริการ NYC Language Institute — แปล · รับรอง · Notary · วีซ่า · กฎหมาย (เริ่ม ฿1,500)";
const DESC =
  "ตารางราคาบริการครบทุกหมวด — แปลเอกสาร /หน้า · Notary Public · MFA · NAATI — ทีมงานตอบไวทุกวัน ปรึกษาก่อนตัดสินใจได้ฟรี";

const PHONE = "083-249-4999";

export const Route = createFileRoute("/pricing")({
  loader: async () => {
    const { SERVICE_HEAD } = await import("@/data/services-head.gen");
    return {
      services: Object.values(SERVICE_HEAD).map((s) => ({
        slug: s.slug,
        nameTh: s.nameTh,
        nameEn: s.nameEn,
        priceFrom: s.priceFrom,
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, "/pricing", "th") },
      { name: "description", content: ctaDesc(DESC, "/pricing", "th") },
      {
        name: "keywords",
        content:
          "ราคาแปลเอกสาร, ค่าบริการ notary, ราคารับรองกงสุล, NAATI ราคา, ค่าวีซ่า, NYC Legal ราคา",
      },
      { property: "og:title", content: ctaTitle(TITLE, "/pricing", "th") },
      { property: "og:description", content: ctaDesc(DESC, "/pricing", "th") },
      { property: "og:url", content: `${SITE}/pricing` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/pricing` }, ...hreflangLinks("/pricing")],
    scripts: [
      speakableSchema(),
      consolidateGraph([
        collectionPageSchema({
          path: "/pricing",
          name: TITLE,
          description: DESC,
          itemCount: (loaderData?.services ?? []).length,
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
              { "@type": "ListItem", position: 2, name: "ราคาบริการ", item: `${SITE}/pricing` },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "@id": `${SITE}/pricing#catalog`,
            name: "NYC Language Institute — Service Price Catalog 2025",
            url: `${SITE}/pricing`,
            provider: {
              "@type": "LegalService",
              name: "NYC Language Institute",
              url: SITE,
              telephone: PHONE,
            },
            itemListElement: (loaderData?.services ?? []).map((s, i) => ({
              "@type": "Offer",
              position: i + 1,
              name: s.nameEn,
              url: `${SITE}/services/${s.slug}`,
              availability: "https://schema.org/InStock",
              priceCurrency: "THB",
              price: s.priceFrom,
              priceSpecification: {
                "@type": "PriceSpecification",
                priceCurrency: "THB",
                price: s.priceFrom,
                minPrice: s.priceFrom,
                valueAddedTaxIncluded: false,
                description: `เริ่มต้น ${s.priceFrom.toLocaleString("en-US")} THB — ราคายังไม่รวม VAT 7%`,
              },
              itemOffered: {
                "@type": "Service",
                name: s.nameEn,
                url: `${SITE}/services/${s.slug}`,
              },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "ราคาที่แสดงรวม VAT แล้วหรือยัง?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "ราคายังไม่รวม VAT 7% — กรณีต้องการใบกำกับภาษีเต็มรูปแบบ เพิ่ม VAT 7% จากยอดสุทธิ ลูกค้าทั่วไปที่ไม่ขอใบกำกับภาษีไม่ต้องเสีย VAT",
                },
              },
              {
                "@type": "Question",
                name: "มีค่ามัดจำเท่าไหร่?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "งานทั่วไปมัดจำ 50% · งานเร่งด่วน (Rush 4 ชม.) ชำระ 100% ล่วงหน้า · งานองค์กรที่มี PO/สัญญารายปี ชำระ Net 30 หลังออกใบกำกับภาษี",
                },
              },
              {
                "@type": "Question",
                name: "ส่วนลดสำหรับลูกค้าองค์กร / งานปริมาณมาก?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "งานเกิน 50 หน้า ลด 10–15% · สัญญา Retainer รายเดือน/รายปี ลดสูงสุด 25% · สำนักงานกฎหมาย/บริษัทแปล ราคา Wholesale ติดต่อทีม Account Manager",
                },
              },
              {
                "@type": "Question",
                name: "รับชำระเงินช่องทางใดบ้าง?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "โอนธนาคารทุกแบงก์ไทย · PromptPay (Bank of Thailand QR Standard) · บัตรเครดิต Visa/Master/JCB/Amex ผ่าน 2C2P (PCI-DSS Level 1) · Stripe สำหรับลูกค้าต่างประเทศ 135+ สกุลเงิน · USDT (TRC20/ERC20) สำหรับ Crypto Business — ออกใบกำกับภาษี e-Tax Invoice ตามประกาศอธิบดี ฉบับ 31/2560 และ ป.รัษฎากร ม.86/4 ทุกการชำระ พร้อม TIN 0105556023896",
                },
              },
              {
                "@type": "Question",
                name: "มีรับประกันคุณภาพและคืนเงินหรือไม่?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "รับประกัน 100% — หากเอกสารถูกปฏิเสธจากหน่วยงานราชการเพราะคำแปลของเรา เราแก้ไขให้ฟรีและคืนเงินค่าธรรมเนียมที่เสียไป",
                },
              },
            ],
          }),
        },
      ]),
    ],
  }),
  component: PricingPage,
});
